
import { useEffect } from 'react';
import { create } from 'zustand';
import { ticketsApi } from '@/shared/api/tickets';
import { apiErrorText } from '@/shared/api';
import { toast } from '@/shared/ui';
import { useTicketsStore } from '@/features/tickets/model/tickets-store';

export interface TicketMuteEntry {
  muted: boolean;
  canMute: boolean;
}

interface MuteState {
  byTicket: Record<string, TicketMuteEntry | undefined>;
  busy: Record<string, boolean | undefined>;
  load: (ticketId: string) => Promise<void>;
  toggle: (ticketId: string) => Promise<boolean | null>;
}

const inFlight = new Map<string, Promise<void>>();

export const useMuteStore = create<MuteState>((set, get) => ({
  byTicket: {},
  busy: {},

  load: (ticketId) => {
    const running = inFlight.get(ticketId);
    if (running) return running;
    const req = ticketsApi
      .getMute(ticketId)
      .then((res) => {
        set((s) => ({
          byTicket: {
            ...s.byTicket,
            [ticketId]: { muted: !!res.data?.muted, canMute: !!res.data?.canMute },
          },
        }));
      })
      .catch(() => {
        set((s) => ({
          byTicket: { ...s.byTicket, [ticketId]: { muted: false, canMute: false } },
        }));
      })
      .finally(() => {
        inFlight.delete(ticketId);
      });
    inFlight.set(ticketId, req);
    return req;
  },

  toggle: async (ticketId) => {
    if (get().busy[ticketId]) return null;
    if (!get().byTicket[ticketId]) await get().load(ticketId);
    const cur = get().byTicket[ticketId];
    if (!cur || !cur.canMute) return null;
    const next = !cur.muted;

    const write = (muted: boolean) => {
      set((s) => ({ byTicket: { ...s.byTicket, [ticketId]: { ...cur, muted } } }));
      useTicketsStore.getState().applyTicketUpdate({ id: ticketId, muted });
    };

    set((s) => ({ busy: { ...s.busy, [ticketId]: true } }));
    write(next);
    try {
      if (next) await ticketsApi.mute(ticketId);
      else await ticketsApi.unmute(ticketId);
      return next;
    } catch (err) {
      write(!next);
      toast.error(apiErrorText(err, 'Не удалось изменить уведомления по заявке'));
      return null;
    } finally {
      set((s) => ({ busy: { ...s.busy, [ticketId]: false } }));
    }
  },
}));

export const useTicketMute = (ticketId: string): TicketMuteEntry | undefined =>
  useMuteStore((s) => s.byTicket[ticketId]);

export function usePrefetchTicketMute(ticketId: string | null | undefined): void {
  const load = useMuteStore((s) => s.load);
  useEffect(() => {
    if (ticketId) void load(ticketId);
  }, [ticketId, load]);
}

export const useTicketMuteBusy = (ticketId: string): boolean =>
  useMuteStore((s) => !!s.busy[ticketId]);
